import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import {
  CreateOrgUserData,
  createSqspOrgUser,
  fetchOrgUserData,
  listOrgUsers,
  listPendingMigrationUsers,
} from '../../apis/sqspUsers';
import {
  NOT_AUTHORIZED_USER_FETCH,
  NOT_AUTHORIZED_USER_INVITE,
  NOT_AUTHORIZED_USER_VIEW,
  SUCCESS_INVITATION,
} from '../../const/messageCodes';
import { INITIAL_LIMIT, PAGINATION_LIMIT_MIN } from '../../const/pagination';
import { PageInfo, PageInfoQueryWithSearch } from '../../models/PaginatedResponse';
import { OrgUser, PendingMigrationUser } from '../../models/SqspUser';
import { reduxErrorHandler } from '../../utils/reduxError';
import { sendSuccessMessageWithCode } from '../messages';
import { RootState } from '../rootReducer';
import { AppThunk } from '../store';

export type OrgUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
  pageInfo: PageInfo;
} & EntityState<OrgUser>;

export type PendingMigrationUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
  pageInfo: PageInfo;
} & EntityState<PendingMigrationUser>;

const orgUsersAdaptor = createEntityAdapter<OrgUser>();

export const orgUsersSlice = createSlice<OrgUsersState, SliceCaseReducers<OrgUsersState>, 'orgUsers'>({
  name: 'orgUsers',
  initialState: orgUsersAdaptor.getInitialState({
    isLoading: true,
    error: null,
    pageInfo: {
      total: 0,
      limit: PAGINATION_LIMIT_MIN,
      offset: 0,
    },
  }),
  reducers: {
    orgUserRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    orgUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    orgUserNotFound(state) {
      state.isLoading = false;
      state.error = null;
    },
    orgUserReceived(state, action: PayloadAction<OrgUser>) {
      orgUsersAdaptor.upsertOne(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    orgUsersReceived(state, action: PayloadAction<OrgUser[]>) {
      orgUsersAdaptor.setAll(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    appendOrgUsers(state, action: PayloadAction<OrgUser[]>) {
      orgUsersAdaptor.upsertMany(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    updatePageInfo(state, action: PayloadAction<PageInfo>) {
      state.pageInfo = action.payload;
      state.isLoading = false;
    },
  },
});

const pendingMigrationUsersAdaptor = createEntityAdapter<PendingMigrationUser>();

export const pendingMigrationUsersSlice = createSlice<
  PendingMigrationUsersState,
  SliceCaseReducers<PendingMigrationUsersState>,
  'pendingMigrationUsers'
>({
  name: 'pendingMigrationUsers',
  initialState: pendingMigrationUsersAdaptor.getInitialState({
    isLoading: true,
    error: null,
    pageInfo: {
      total: 0,
      limit: PAGINATION_LIMIT_MIN,
      offset: 0,
    },
  }),
  reducers: {
    pendingMigrationUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    pendingMigrationUsersReceived(state, action: PayloadAction<PendingMigrationUser[]>) {
      pendingMigrationUsersAdaptor.setAll(state, action.payload);
    },
    appendPendingMigrationUsers(state, action: PayloadAction<PendingMigrationUser[]>) {
      pendingMigrationUsersAdaptor.upsertMany(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    updatePageInfo(state, action: PayloadAction<PageInfo>) {
      state.pageInfo = action.payload;
      state.isLoading = false;
    },
  },
});

// Selectors
export const {
  selectAll: selectAllOrgUsers,
  selectById: selectOrgUserById,
  selectTotal: selectTotalOrgUsers,
} = orgUsersAdaptor.getSelectors();
export const {
  selectAll: selectAllPendingMigrationUsers,
  selectById: selectPendingMigrationUserById,
  selectTotal: selectTotalPendingMigrationUsers,
} = pendingMigrationUsersAdaptor.getSelectors();

export const filterSingleUser = (state: RootState, id: number) => {
  const foundUser = selectAllOrgUsers(state.orgUsers).filter((orgUser: OrgUser) => orgUser.id === id);
  if (foundUser.length) {
    return foundUser[0];
  }
  return null;
};

export const { reducer: orgUsersReducer } = orgUsersSlice;
export const { reducer: pendingMigrationUsersReducer } = pendingMigrationUsersSlice;

export const {
  orgUserRequested,
  orgUsersRequested,
  orgUserReceived,
  orgUsersReceived,
  appendOrgUsers,
  orgUserNotFound,
  errorReceived: orgUsersErrorReceived,
  updatePageInfo: updateOrgUsersPageInfo,
} = orgUsersSlice.actions;

export const {
  pendingMigrationUsersRequested,
  pendingMigrationUserReceived,
  pendingMigrationUsersReceived,
  appendPendingMigrationUsers,
  errorReceived: pendingMigrationUsersErrorReceived,
  updatePageInfo: updatePendingMigrationUsersPageInfo,
} = pendingMigrationUsersSlice.actions;

export const fetchBackgroundOrgUsers = (enterpriseId: number, options?: Partial<PageInfoQueryWithSearch>): AppThunk => {
  return async (dispatch) => {
    try {
      if (options?.offset && options.offset >= INITIAL_LIMIT) {
        const orgUsersResponse = await listOrgUsers(enterpriseId, options);
        dispatch(appendOrgUsers(orgUsersResponse.data));
        dispatch(updateOrgUsersPageInfo(orgUsersResponse.pageInfo));

        if (options.offset + orgUsersResponse.data.length < orgUsersResponse.pageInfo.total) {
          dispatch(
            fetchBackgroundOrgUsers(enterpriseId, {
              ...options,
              offset: options.offset + orgUsersResponse.data.length,
            }),
          );
        }
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, orgUsersErrorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const fetchOrgUsers = (enterpriseId: number, options?: PageInfoQueryWithSearch): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(orgUsersRequested(undefined));
      const orgUsers = await listOrgUsers(enterpriseId, options);
      dispatch(orgUsersReceived(orgUsers.data));
      dispatch(updateOrgUsersPageInfo(orgUsers.pageInfo));

      if (orgUsers.data.length < orgUsers.pageInfo.total) {
        dispatch(fetchBackgroundOrgUsers(enterpriseId, { ...options, offset: orgUsers.data.length }));
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, orgUsersErrorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const fetchBackgroundPendingMigrationUsers = (
  enterpriseId: number,
  options?: Partial<PageInfoQueryWithSearch>,
): AppThunk => {
  return async (dispatch) => {
    try {
      if (options?.offset && options.offset >= INITIAL_LIMIT) {
        const pendingMigrationUsersResponse = await listPendingMigrationUsers(enterpriseId, options);
        dispatch(appendPendingMigrationUsers(pendingMigrationUsersResponse.data));
        dispatch(updatePendingMigrationUsersPageInfo(pendingMigrationUsersResponse.pageInfo));

        if (options.offset + pendingMigrationUsersResponse.data.length < pendingMigrationUsersResponse.pageInfo.total) {
          dispatch(
            fetchBackgroundPendingMigrationUsers(enterpriseId, {
              ...options,
              offset: options.offset + pendingMigrationUsersResponse.data.length,
            }),
          );
        }
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, pendingMigrationUsersErrorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const fetchPendingMigrationUsers = (enterpriseId: number, options?: PageInfoQueryWithSearch): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(pendingMigrationUsersRequested(undefined));
      const pendingMigrationUsers = await listPendingMigrationUsers(enterpriseId, options);
      dispatch(pendingMigrationUsersReceived(pendingMigrationUsers.data));
      dispatch(updatePendingMigrationUsersPageInfo(pendingMigrationUsers.pageInfo));

      if (pendingMigrationUsers.data.length < pendingMigrationUsers.pageInfo.total) {
        dispatch(
          fetchBackgroundPendingMigrationUsers(enterpriseId, { ...options, offset: pendingMigrationUsers.data.length }),
        );
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, pendingMigrationUsersErrorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };
};

export const fetchOrgUser = (enterpriseId: number, userId: number): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(orgUserRequested(undefined));
      const orgUser = await fetchOrgUserData(enterpriseId, userId);
      if (orgUser) {
        dispatch(orgUserReceived(orgUser));
      } else {
        dispatch(orgUserNotFound(null));
      }
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, orgUsersErrorReceived, NOT_AUTHORIZED_USER_FETCH);
    }
  };
};

// TODO: Remove with AE-634
export const createOrgUser = (enterpriseId: number, userData: CreateOrgUserData): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(orgUserRequested(undefined));
      const orgUser = await createSqspOrgUser(enterpriseId, userData);
      dispatch(sendSuccessMessageWithCode(SUCCESS_INVITATION, { user: userData.name }));
      dispatch(orgUserReceived(orgUser));
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, orgUsersErrorReceived, NOT_AUTHORIZED_USER_INVITE);
    }
  };
};
